import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Controller } from 'react-hook-form'
import type { Control, FieldErrors, UseFormRegister } from 'react-hook-form'

import { TransportType } from '#/shared/api/generated/shipment/models'
import { MANAGE_FIELD_KEY, useIsFieldHidden } from '#/shared/lib/manage-fields'
import { DatePicker } from '#/shared/ui/date-picker'
import { FormCard } from '#/shared/ui/form-card'
import { Input } from '#/shared/ui/input'
import { PriceCheckBadge } from '#/shared/ui/price-check-badge'
import { Select } from '#/shared/ui/select'

import { SourceCombobox } from '#/features/order-create'
import type { PaymentFormData, ShipmentFormData } from '#/features/order-create'

const FIELD_PROPS = {
  inputSize: 'sm' as const,
  labelSize: 'sm' as const,
  clearable: true,
}

const MONEY_PROPS = {
  ...FIELD_PROPS,
  mask: '$',
  numberFormat: true,
  placeholder: '0.00',
}

const TRAILER_VALUES = [
  TransportType.OPEN,
  TransportType.ENCLOSED,
  TransportType.DRIVE_AWAY,
] as const

interface QuoteShipmentCardProps {
  shipmentControl: Control<ShipmentFormData>
  shipmentRegister: UseFormRegister<ShipmentFormData>
  shipmentErrors: FieldErrors<ShipmentFormData>
  paymentControl: Control<PaymentFormData>
  paymentErrors: FieldErrors<PaymentFormData>
  onPriceCheck?: () => void
  /** "Check rate" control, injected by `OrderForm` (self-hides without a shipment). */
  rateCheck?: ReactNode
}

function useTrailerOptions() {
  const { t } = useTranslation()
  return TRAILER_VALUES.map((value) => ({
    value,
    label: t(`orderCreate.shipment.trailerOptions.${value}`),
  }))
}

function ShipDateField({
  control,
  errors,
}: {
  control: Control<ShipmentFormData>
  errors: FieldErrors<ShipmentFormData>
}) {
  const { t } = useTranslation()
  return (
    <Controller
      control={control}
      name="shipDate"
      render={({ field }) => (
        <DatePicker
          instantApply
          labelSize="sm"
          align="left"
          label={t('orderCreate.shipment.shipDate')}
          dateFormat="MMM D"
          value={field.value}
          onValueChange={field.onChange}
          error={!!errors.shipDate}
        />
      )}
    />
  )
}

function TrailerField({
  control,
  errors,
}: {
  control: Control<ShipmentFormData>
  errors: FieldErrors<ShipmentFormData>
}) {
  const { t } = useTranslation()
  const trailerOptions = useTrailerOptions()
  return (
    <Controller
      control={control}
      name="trailer"
      render={({ field }) => (
        <Select
          selectSize="sm"
          labelSize="sm"
          label={t('orderCreate.shipment.trailer')}
          placeholder={t('orderCreate.shipment.placeholders.trailer')}
          options={trailerOptions}
          value={field.value}
          onValueChange={field.onChange}
          error={!!errors.trailer}
        />
      )}
    />
  )
}

function SourceField({
  control,
  errors,
}: {
  control: Control<ShipmentFormData>
  errors: FieldErrors<ShipmentFormData>
}) {
  const { t } = useTranslation()
  return (
    <Controller
      control={control}
      name="source"
      render={({ field }) => (
        <SourceCombobox
          label={t('orderCreate.shipment.source')}
          value={field.value}
          onValueChange={field.onChange}
          error={!!errors.source}
        />
      )}
    />
  )
}

function MoneyField({
  name,
  label,
  control,
  errors,
}: {
  name: 'totalTariff' | 'deposit'
  label: string
  control: Control<PaymentFormData>
  errors: FieldErrors<PaymentFormData>
}) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <Input
          {...MONEY_PROPS}
          label={label}
          value={field.value}
          onChange={(e) => field.onChange(e.target.value)}
          onBlur={field.onBlur}
          name={field.name}
          error={!!errors[name]}
        />
      )}
    />
  )
}

export function QuoteShipmentCard({
  shipmentControl,
  shipmentRegister,
  shipmentErrors,
  paymentControl,
  paymentErrors,
  onPriceCheck,
  rateCheck,
}: QuoteShipmentCardProps) {
  const { t } = useTranslation()
  const showCustomerNote = !useIsFieldHidden(
    MANAGE_FIELD_KEY.shipmentCustomerNote,
  )
  return (
    <FormCard
      title={t('orderCreate.shipment.title')}
      actions={
        <div className="flex items-center gap-3">
          {rateCheck}
          <PriceCheckBadge
            label={t('orderCreate.payment.priceCheck')}
            onClick={onPriceCheck}
          />
        </div>
      }
      contentClassName="gap-2.5"
    >
      <div className="flex items-start gap-2.5">
        <div className="flex-1">
          <ShipDateField control={shipmentControl} errors={shipmentErrors} />
        </div>
        <div className="flex-1">
          <TrailerField control={shipmentControl} errors={shipmentErrors} />
        </div>
        <div className="flex-1">
          <SourceField control={shipmentControl} errors={shipmentErrors} />
        </div>
      </div>
      <div className="flex items-start gap-2.5">
        <div className="flex-1">
          <MoneyField
            name="totalTariff"
            label={`${t('orderCreate.payment.totalTariff')} *`}
            control={paymentControl}
            errors={paymentErrors}
          />
        </div>
        <div className="flex-1">
          <MoneyField
            name="deposit"
            label={`${t('orderCreate.payment.deposit')} *`}
            control={paymentControl}
            errors={paymentErrors}
          />
        </div>
      </div>
      {showCustomerNote && (
        <Input
          {...FIELD_PROPS}
          {...shipmentRegister('customerNote')}
          label={t('orderCreate.shipment.customerNote')}
          error={!!shipmentErrors.customerNote}
        />
      )}
    </FormCard>
  )
}
